import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Spinner, Alert, Button, Badge, Dropdown } from "react-bootstrap";
import { FaEdit, FaComments, FaFileInvoice, FaStar, FaComment, FaCog, FaChartBar, FaClipboardCheck } from "react-icons/fa";
import "../CSS/Dashboard.css";
import EmployeeSidebar from "../../components/Sidebar";
import Chatbox from "../Chatbox/Chatbox";
import ClientPagination from "./ClientPagination";
import ClientFilters from "./ClientFilters";
import ClientMenuModal from "./ClientMenuModal";
import EditClientModal from "./EditClientModal";
import ChecklistModal from "./ChecklistModal";

const EmployeeDashboard = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [chatUser, setChatUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [selectedMenuUser, setSelectedMenuUser] = useState(null);
  const [stepFilter, setStepFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [setShowStepModal] = useState(false);
  const [selectedStepUser, setSelectedStepUser] = useState(null);
  const [selectedStep, setSelectedStep] = useState(2);
  const [userSteps, setUserSteps] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(15);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", email: "" });
  const [employee, setEmployee] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistUser, setChecklistUser] = useState(null);
  const [checklistMemberIndex, setChecklistMemberIndex] = useState(-1);
  const [checklistFormType, setChecklistFormType] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [clients, stepFilter, searchQuery, paymentFilter, serviceFilter]);

  const fetchClients = async () => {
    const token = localStorage.getItem("employeeToken");
    if (!token) {
      navigate("/employee-login");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to fetch clients");

      const data = await response.json();
      setEmployee(data.employee);
      const sortedClients = data.employee.clients.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setClients(sortedClients);
      sortedClients.forEach((client) => {
        fetchUserSteps(client._id);
        fetchUserMemberSteps(client._id);
      });
      setFilteredClients(sortedClients);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSteps = async (userId) => {
    const token = localStorage.getItem("employeeToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user/${userId}/steps`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch user steps");

      const data = await response.json();
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          steps: data.steps
        }
      }));
    } catch (error) {
      setError(error.message);
    }
  };

  const fetchUserMemberSteps = async (userId) => {
    const token = localStorage.getItem("employeeToken");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/user/${userId}/member-steps/0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) throw new Error("Failed to fetch user member steps");

      const data = await response.json();
      setUserSteps((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          memberSteps: data.memberSteps
        }
      }));
    } catch (error) {
      console.error("Error fetching member steps:", error);
      // Don't set global error for this - just log it
    }
  };

  const filterClients = () => {
    let filtered = clients;

    if (stepFilter !== "all") {
      filtered = filtered.filter((client) => {
        const steps = userSteps[client._id]?.steps || [];
        const memberSteps = userSteps[client._id]?.memberSteps || [];
        return [...steps, ...memberSteps].some((step) =>
          step.step === parseInt(stepFilter) && step.status === "submitted"
        );
      });
    }

    if (serviceFilter !== "all") {
      filtered = filtered.filter((client) => {
        switch (serviceFilter) {
          case "registeredAgent":
            return client.isRegisteredAgentEnabled;
          case "usa":
            return client.isUSAEnabled;
          case "tax":
            return client.isTaxFilingEnabled;
          case "bookkeeping":
            return client.isBookkeepingEnabled;
          case "cfo":
            return client.isCFOEnabled;
          default:
            return true;
        }
      });
    }

    if (paymentFilter !== "all") {
      filtered = filtered.filter((client) => {
        const hasPaid = client.isUSAEnabled || client.isTaxFilingEnabled ||
          client.isBookkeepingEnabled || client.isCFOEnabled || client.isRegisteredAgentEnabled;
        return paymentFilter === "paid" ? hasPaid : !hasPaid;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((client) =>
        client.name.toLowerCase().includes(query) ||
        client.email.toLowerCase().includes(query) ||
        (client.members && client.members.some(m => m.name.toLowerCase().includes(query)))
      );
    }

    setFilteredClients(filtered);
    setCurrentPage(1);
  };

  const isNewClient = (createdAt) => {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 3);
    return new Date(createdAt) > oneWeekAgo;
  };

  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentClients = filteredClients.slice(indexOfFirstClient, indexOfLastClient);
  const totalPages = Math.ceil(filteredClients.length / clientsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEdit = (client) => {
    setSelectedUser(client);
    setEditFormData({ name: client.name, email: client.email });
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/users/${selectedUser._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) throw new Error("Failed to update user");

      const updatedUser = await response.json();
      setClients(clients.map((client) =>
        client._id === updatedUser.user._id ? updatedUser.user : client
      ));
      setShowEditModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const toggleChatbox = (user) => {
    setChatUser(chatUser ? null : user);
  };

  const handleMenuClick = (user) => {
    setSelectedMenuUser(user);
    setSelectedStepUser(user);
    setShowMenu(true);
  };

  const handleInvoiceClick = (user) => {
    navigate(`/employee-documents/${user._id}`);
  };

  const handleBookkeepingClick = (user) => {
    navigate(`/employee-bookkeeping/${user._id}`);
  };

  const handlePrepareChecklist = (user, memberIndex = -1) => {
    if (!user) {
      alert('Error: User data is missing');
      return;
    }
    
    const formType = memberIndex === -1 
      ? user.assignedForm 
      : (user.members && user.members[memberIndex] ? user.members[memberIndex].assignedForm : null);
    
    if (!formType) {
      alert('No form assigned. Please assign a form first.');
      return;
    }
    
    setChecklistUser(user);
    setChecklistMemberIndex(memberIndex);
    setChecklistFormType(formType);
    setShowChecklistModal(true);
  };

  const handleFormAssignment = async (userId, formNumber) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign form");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleMemberFormAssignment = async (userId, memberIndex, formNumber) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/assign-member-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, formNumber }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign member form");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const handleStepSubmit = async () => {
    if (!selectedStepUser) {
      setError("No user selected for step submission");
      return;
    }

    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            step: selectedStep
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit step");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      await fetchUserSteps(selectedStepUser._id);
      setShowStepModal(false);
      alert(`Step ${selectedStep} submitted successfully!`);

      if (data.nextStep) {
        alert(`Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const handleMemberStepSubmit = async (memberIndex) => {
    if (!selectedStepUser) {
      setError("No user selected for member step submission");
      return;
    }

    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/submit-member-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            memberIndex,
            step: selectedStep
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit member step");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      await fetchUserMemberSteps(selectedStepUser._id);
      setShowStepModal(false);
      alert(`Member ${memberIndex + 1} Step ${selectedStep} submitted successfully!`);

      if (data.nextStep) {
        alert(`Member ${memberIndex + 1} Step ${data.nextStep} has been unlocked for the client!`);
      }
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const isStepSubmitted = (user, step) => {
    if (!user || !userSteps[user._id]?.steps) return false;
    const stepInfo = userSteps[user._id].steps.find(s => s.step === step);
    return stepInfo && stepInfo.status === 'submitted';
  };

  const isMemberStepSubmitted = (user, memberIndex, step) => {
    if (!user || !userSteps[user._id]?.memberSteps?.[memberIndex]) return false;
    const stepInfo = userSteps[user._id].memberSteps[memberIndex].find(s => s.step === step);
    return stepInfo && stepInfo.status === 'submitted';
  };

  const canSubmitStep = (user, step) => {
    if (!user || !userSteps[user._id]?.steps) return false;
    if (step === 1) return true;

    for (let i = 1; i < step; i++) {
      const prevStep = userSteps[user._id].steps.find(s => s.step === i);
      if (!prevStep || prevStep.status !== 'submitted') {
        return false;
      }
    }
    return true;
  };

  const canSubmitMemberStep = (user, memberIndex, step) => {
    if (!user || !userSteps[user._id]?.memberSteps?.[memberIndex]) return false;
    if (step === 1) return true;

    for (let i = 1; i < step; i++) {
      const prevStep = userSteps[user._id].memberSteps[memberIndex].find(s => s.step === i);
      if (!prevStep || prevStep.status !== 'submitted') {
        return false;
      }
    }
    return true;
  };

  // USA Company Formation functions
  const handleUSAFormationStepSubmit = async (stepNumber) => {
    if (!selectedStepUser) {
      setError("No user selected for USA formation step submission");
      return;
    }

    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/admin/submit-step`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: selectedStepUser._id,
            step: stepNumber
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit USA formation step");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      
      alert(`USA Formation Step ${stepNumber} submitted successfully!`);

      if (data.nextStep) {
        alert(`Step ${data.nextStep} has been unlocked for the client!`);
      }
      
      // Refresh client data to ensure we have the latest state
      fetchClients();
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const isUSAFormationStepSubmitted = (userId, stepNumber) => {
    const client = clients.find(c => c._id === userId);
    if (!client || !client.companySteps) return false;
    
    const step = client.companySteps.find(s => s.step === stepNumber);
    return step && step.status === 'submitted';
  };

  const canSubmitUSAFormationStep = (userId, stepNumber) => {
    const client = clients.find(c => c._id === userId);
    if (!client || !client.companySteps) return false;
    
    // Step 1 can only be submitted by user
    if (stepNumber === 1) return false;
    
    // For steps 2 and 3, check if previous step is submitted
    if (stepNumber > 1) {
      const previousStep = client.companySteps.find(s => s.step === stepNumber - 1);
      if (!previousStep || previousStep.status !== 'submitted') return false;
    }
    
    const currentStep = client.companySteps.find(s => s.step === stepNumber);
    return currentStep && currentStep.status !== 'submitted';
  };

  const handleCompanyFormAssignment = async (userId, formType) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/admin/assign-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, formType }),
        }
      );

      if (!response.ok) throw new Error("Failed to assign company form");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      alert(`${formType} form assigned successfully!`);
    } catch (error) {
      setError(error.message);
      alert(error.message);
    }
  };

  const areAllStepsCompleted = (userId) => {
    if (!userSteps[userId]?.steps) return false;

    const steps = userSteps[userId].steps;
    
    // Check if user completed all 5 steps in the old system
    const completedAllOldSteps = steps.length >= 5 && 
      steps.every(step => step.status === 'submitted' || step.status === 'completed');
    
    if (completedAllOldSteps) return true;
    
    // Check for 4 completed steps in the new system
    const relevantSteps = steps.filter(step => step.step <= 4);
    const completedSteps = relevantSteps.filter(
      step => step.status === 'submitted' || step.status === 'completed'
    ).length;

    return completedSteps >= 4;
  };

  const areAllMemberStepsCompleted = (userId, memberIndex) => {
    if (!userSteps[userId]?.memberSteps?.[memberIndex]) return false;
    
    const steps = userSteps[userId].memberSteps[memberIndex];
    
    // Check if member completed all 5 steps in the old system
    const completedAllOldSteps = steps.length >= 5 && 
      steps.every(step => step.status === 'submitted' || step.status === 'completed');
    
    if (completedAllOldSteps) return true;
    
    // Check for 4 completed steps in the new system
    const relevantSteps = steps.filter(step => step.step <= 4);
    const completedSteps = relevantSteps.filter(
      step => step.status === 'submitted' || step.status === 'completed'
    ).length;

    return completedSteps >= 4;
  };

  const renderStepsProgress = (userId, memberIndex = null) => {
    if (!userSteps[userId]) return null;

    // For primary user steps
    if (memberIndex === null) {
      if (areAllStepsCompleted(userId)) {
        return <Badge bg="success">Completed</Badge>;
      }

      const steps = userSteps[userId]?.steps || [];
      const inProgressSteps = steps.filter(step => step.status === "in-progress");

      if (inProgressSteps.length === 0) {
        return <Badge bg="warning">Not Started</Badge>;
      }

      return inProgressSteps.map((step) => (
        <Badge
          key={`primary-${step.step}`}
          bg="danger"
          className="me-1"
        >
          Step {step.step}: {step.status}
        </Badge>
      ));
    }

    // For member steps
    if (userSteps[userId]?.memberSteps?.[memberIndex]) {
      if (areAllMemberStepsCompleted(userId, memberIndex)) {
        return <Badge bg="success">Completed</Badge>;
      }

      const steps = userSteps[userId].memberSteps[memberIndex];
      const inProgressSteps = steps.filter(step => step.status === "in-progress");

      if (inProgressSteps.length === 0) {
        return <Badge bg="warning">Not Started</Badge>;
      }

      return inProgressSteps.map((step) => (
        <Badge
          key={`member-${memberIndex}-${step.step}`}
          bg="danger"
          className="me-1"
        >
          Step {step.step}: {step.status}
        </Badge>
      ));
    }

    return <Badge bg="warning">Not Started</Badge>;
  };

  // Render USA Company Formation steps progress
  const renderUSAFormationProgress = (userId) => {
    const client = clients.find(c => c._id === userId);
    if (!client || !client.companySteps) {
      return <Badge bg="warning">Not Started</Badge>;
    }

    // Check if all steps are completed
    const allStepsCompleted = client.companySteps.every(step => step.status === 'completed');
    if (allStepsCompleted && client.companySteps.length > 0) {
      return <Badge bg="success">Completed</Badge>;
    }

    // Find in-progress steps
    const inProgressSteps = client.companySteps.filter(step => 
      step.status === 'in-progress' || 
      (step.status !== 'completed' && step.step > 1 && 
       client.companySteps.some(s => s.step === step.step - 1 && s.status === 'completed'))
    );

    if (inProgressSteps.length === 0) {
      // If no steps are in progress but some steps exist
      if (client.companySteps.some(step => step.status === 'completed')) {
        const lastCompletedStep = Math.max(...client.companySteps
          .filter(step => step.status === 'completed')
          .map(step => step.step));
        
        const nextStep = lastCompletedStep + 1;
        if (nextStep <= 3) { // Assuming 3 steps total for USA Formation
          return (
            <Badge bg="info" className="me-1">
              Ready for Step {nextStep}
            </Badge>
          );
        }
      }
      return <Badge bg="warning">Not Started</Badge>;
    }

    return inProgressSteps.map((step) => (
      <Badge
        key={`usa-formation-${step.step}`}
        bg="danger"
        className="me-1"
      >
        Step {step.step}: {step.status === 'in-progress' ? 'in-progress' : 'ready'}
      </Badge>
    ));
  };

  const handleMemberNameUpdate = async (userId, memberIndex, name) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/update-member-name`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex, name }),
        }
      );

      if (!response.ok) throw new Error("Failed to update member name");

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const toggleMemberSection = async (userId, isEnabled) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/toggle-member-section`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, isEnabled }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle member section");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
    }
  };

  const addMember = async (userId) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/add-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to add member");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      return data.user.members.length - 1;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const removeMember = async (userId, memberIndex) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/remove-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId, memberIndex }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to remove member");
      }

      const data = await response.json();
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const toggleServiceLock = async (userId, serviceName, isEnabled) => {
    const token = localStorage.getItem("employeeToken");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/toggle-service`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userId, serviceName, isEnabled }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to toggle service");
      }

      const data = await response.json();
      
      setClients(clients.map((client) =>
        client._id === data.user._id ? data.user : client
      ));
      
      if (selectedMenuUser && selectedMenuUser._id === data.user._id) {
        setSelectedMenuUser(data.user);
      }
      
      console.log(`Service ${serviceName} ${isEnabled ? 'enabled' : 'disabled'} successfully`);
      
    } catch (error) {
      console.error("Service toggle error:", error);
      setError(error.message);
      alert(`Failed to ${isEnabled ? 'enable' : 'disable'} ${serviceName}: ${error.message}`);
    }
  }

  const renderClientRows = () => {
    return currentClients.map((client, index) => {
      const absoluteIndex = indexOfFirstClient + index;

      return (
        <React.Fragment key={client._id}>
          {/* Primary client row - always show */}
          <tr>
            <td>{absoluteIndex + 1}</td>
            <td>
              <div className="user-info">
                <span className="full-name">
                  {client.name}
                  {isNewClient(client.createdAt) && (
                    <Badge bg="primary" className="ms-2">New</Badge>
                  )}
                </span>
                <span className="email">{client.email}</span>
              </div>
            </td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>-</td>
            <td>
              <div className="action-dropdown">
                <Dropdown>
                  <Dropdown.Toggle variant="secondary" size="sm" id="dropdown-basic">
                    Actions
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => toggleChatbox(client)}>
                      <FaComments className="me-2" /> Chat
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleInvoiceClick(client)}>
                      <FaFileInvoice className="me-2" /> Documents
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleMenuClick(client)}>
                      <FaCog className="me-2" /> Menu
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleBookkeepingClick(client)}>
                      <FaChartBar className="me-2" /> Bookkeeping
                    </Dropdown.Item>   
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </td>
          </tr>

          {/* Service rows - only show if service is enabled */}
          {[
            { key: 'isUSAEnabled', name: 'USA Company ' },
            { key: 'isTaxFilingEnabled', name: 'Tax Filing' },
            { key: 'isBookkeepingEnabled', name: 'Bookkeeping' },
            { key: 'isCFOEnabled', name: 'CFO Advisory' },
            { key: 'isRegisteredAgentEnabled', name: 'Registered Agent' }
          ].map(service => (
            client[service.key] && (
              <tr key={`${client._id}-${service.key}`} className="service-row">
                <td></td>
                <td>
                  <div className="user-info">
                    <span className="full-name">
                      {client.name} - {service.name}
                    </span>
                  </div>
                </td>
                <td>
                  <Badge bg="success">Paid</Badge>
                </td>
                <td>{employee?.name || "-"}</td>
                <td>{service.name}</td>
                <td>
                  {service.key === 'isTaxFilingEnabled' && client.assignedForm ? (
                    <Badge bg="primary">{client.assignedForm}</Badge>
                  ) : (
                    <span className="text-muted">No</span>
                  )}
                </td>
                <td>
                  {client.feedback && client.feedback.rating ? (
                    <div className="star-rating">
                      {[...Array(5)].map((_, i) => (
                        <FaStar
                          key={i}
                          color={i < client.feedback.rating ? "#ffc107" : "#e4e5e9"}
                        />
                      ))}
                      {client.feedback.comment && (
                        <span
                          className="ms-2 feedback-comment-icon"
                          title={client.feedback.comment}
                          onClick={() => alert(`Feedback: ${client.feedback.comment}`)}
                        >
                          <FaComment />
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">No rating</span>
                  )}
                </td>
                <td>
                  {service.key === 'isTaxFilingEnabled' ? renderStepsProgress(client._id) : 
                   service.key === 'isUSAEnabled' ? renderUSAFormationProgress(client._id) : "-"}
                </td>
                <td>
                  {service.key === 'isTaxFilingEnabled' && client.assignedForm && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePrepareChecklist(client)}
                    >
                      <FaClipboardCheck className="me-1" /> Prepare
                    </Button>
                  )}
                </td>
                <td>-</td>
              </tr>
            )
          ))}

          {/* Member rows if enabled */}
          {client.members && client.members.map((member, memberIndex) => (
            <tr key={`${client._id}-member-${memberIndex}`} className="member-row">
              <td></td>
              <td>
                <div className="user-info">
                  <span className="full-name">
                    {member.name || `Member ${memberIndex + 1}`}
                  </span>
                </div>
              </td>
              <td>
                <Badge bg="success">Paid</Badge>
              </td>
              <td>{employee?.name || "-"}</td>
              <td>Tax Filing</td>
              <td>
                {member.assignedForm ? (
                  <Badge bg="primary">{member.assignedForm}</Badge>
                ) : (
                  <span className="text-muted">Not assigned</span>
                )}
              </td>
              <td>
                <span className="text-muted">No rating</span>
              </td>
              <td>
                {client.isTaxFilingEnabled ? renderStepsProgress(client._id, memberIndex) : "-"}
              </td>
              <td>
                {client.isTaxFilingEnabled && member.assignedForm && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handlePrepareChecklist(client, memberIndex)}
                  >
                    <FaClipboardCheck className="me-1" /> Prepare
                  </Button>
                )}
              </td>
              <td></td>
            </tr>
          ))}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="admin-dashboard-container">
      <EmployeeSidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        employee={employee}
      />

      <h2 className="dashboard-title mb-4">Client Management</h2>

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading clients...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <>
          <ClientFilters
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            stepFilter={stepFilter}
            setStepFilter={setStepFilter}
            paymentFilter={paymentFilter}
            setPaymentFilter={setPaymentFilter}
            serviceFilter={serviceFilter}
            setServiceFilter={setServiceFilter}
          />

          <div className="table-responsive excel-table">
            <Table striped bordered hover className="users-table">
              <thead className="table-header">
                <tr>
                  <th>Sr No.</th>
                  <th>Client Information</th>
                  <th>P / U</th>
                  <th>Emp.Assign</th>
                  <th>Service Name</th>
                  <th>Form Name</th>
                  <th>Emp.rating (client)</th>
                  <th>Steps WIP</th>
                  <th>Prepare Checklist</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {renderClientRows()}
              </tbody>
            </Table>
          </div>

          <ClientPagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={paginate}
            filteredClients={filteredClients}
            clientsPerPage={clientsPerPage}
            indexOfFirstClient={indexOfFirstClient}
            indexOfLastClient={indexOfLastClient}
          />
        </>
      )}

      <EditClientModal
        showEditModal={showEditModal}
        setShowEditModal={setShowEditModal}
        selectedUser={selectedUser}
        editFormData={editFormData}
        setEditFormData={setEditFormData}
        handleEditSubmit={handleEditSubmit}
      />

      <ClientMenuModal
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        selectedMenuUser={selectedMenuUser}
        setSelectedMenuUser={setSelectedMenuUser}
        selectedStep={selectedStep}
        setSelectedStep={setSelectedStep}
        handleFormAssignment={handleFormAssignment}
        handleMemberFormAssignment={handleMemberFormAssignment}
        handleStepSubmit={handleStepSubmit}
        handleMemberStepSubmit={handleMemberStepSubmit}
        isStepSubmitted={isStepSubmitted}
        isMemberStepSubmitted={isMemberStepSubmitted}
        canSubmitStep={canSubmitStep}
        canSubmitMemberStep={canSubmitMemberStep}
        toggleServiceLock={toggleServiceLock}
        handleMemberNameUpdate={handleMemberNameUpdate}
        addMember={addMember}
        removeMember={removeMember}
        handleUSAFormationStepSubmit={handleUSAFormationStepSubmit}
        isUSAFormationStepSubmitted={isUSAFormationStepSubmitted}
        canSubmitUSAFormationStep={canSubmitUSAFormationStep}
        handleCompanyFormAssignment={handleCompanyFormAssignment}
        userSteps={userSteps}
      />

      {chatUser && (
        <Chatbox
          user={chatUser}
          onClose={() => setChatUser(null)}
        />
      )}

      <ChecklistModal
        show={showChecklistModal}
        onHide={() => setShowChecklistModal(false)}
        userId={checklistUser?._id}
        memberIndex={checklistMemberIndex}
        formType={checklistFormType}
      />
    </div>
  );
};

export default EmployeeDashboard;